import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Tooltip,
  Fade,
} from '@mui/material';
import {
  Add,
  Delete,
  School,
  AttachMoney,
  LocationOn,
  Schedule,
  Work,
  ArrowForward,
  ArrowBack,
  CheckCircle,
  Edit,
  Visibility,
  Close,
  Refresh,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { apiGet, apiPost, apiPut, apiDelete, handleApiResponse } from '../../services/api';
import { PaymentType, JobStatus, JobType, ExperienceLevel, EducationLevel } from '../../types/job';

interface TeachingJob {
  _id?: string;
  title: string;
  description: string;
  company: string;
  location: string;
  jobType: JobType;
  category: string;
  experienceLevel: ExperienceLevel;
  educationLevel: EducationLevel;
  subjectsToTeach: string[];
  paymentType: PaymentType;
  paymentRate: {
    min: number;
    max: number;
    currency: string;
  };
  teachingMode: 'online' | 'in_person' | 'hybrid';
  teachingLevel: string[];
  classSize?: string;
  curriculum?: string;
  languageRequirements: string[];
  certificationRequired: string[];
  requirements: string[];
  responsibilities: string[];
  applicationDeadline?: Date;
  status: JobStatus;
  isTeachingJob: boolean;
}

const steps = ['Basic Information', 'Teaching Details', 'Payment & Requirements', 'Review & Create'];

const TEACHING_LEVELS = [
  'Primary School',
  'Middle School',
  'High School',
  'College/University',
  'Adult Education',
  'Professional Training',
];

const COMMON_SUBJECTS = [
  'Mathematics',
  'Physics',
  'Chemistry',
  'Biology',
  'English',
  'French',
  'Kinyarwanda',
  'History',
  'Geography',
  'Computer Science',
  'Economics',
  'Business Studies',
  'Accounting',
  'Psychology',
  'Sociology',
  'Law',
  'Medicine',
  'Engineering',
  'Data Science',
  'AI/Machine Learning',
];

const LANGUAGES = [
  'English',
  'French',
  'Kinyarwanda',
  'Swahili',
  'Spanish',
  'German',
  'Chinese',
  'Arabic',
];

const CERTIFICATIONS = [
  'Teaching Certificate',
  'TEFL/TESOL',
  'Bachelor\'s Degree',
  'Master\'s Degree',
  'PhD',
  'Professional Certification',
  'Subject Matter Expert',
];

const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'RWF', symbol: 'RWF', name: 'Rwandan Franc' },
];

const TeachingJobManagement: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [jobs, setJobs] = useState<TeachingJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit' | 'view'>('create');
  const [selectedJob, setSelectedJob] = useState<TeachingJob | null>(null);

  const [formData, setFormData] = useState<TeachingJob>({
    title: '',
    description: '',
    company: 'Excellence Coaching Hub',
    location: '',
    jobType: JobType.PART_TIME,
    category: 'teaching',
    experienceLevel: ExperienceLevel.MID_LEVEL,
    educationLevel: EducationLevel.BACHELOR,
    subjectsToTeach: [],
    paymentType: PaymentType.PER_HOUR,
    paymentRate: {
      min: 0,
      max: 0,
      currency: 'USD',
    },
    teachingMode: 'online',
    teachingLevel: [],
    classSize: '',
    curriculum: '',
    languageRequirements: [],
    certificationRequired: [],
    requirements: [],
    responsibilities: [],
    status: JobStatus.ACTIVE,
    isTeachingJob: true,
  });

  const [newSubject, setNewSubject] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [newResponsibility, setNewResponsibility] = useState('');

  useEffect(() => {
    loadTeachingJobs();
  }, []);

  const loadTeachingJobs = async () => {
    try {
      setLoading(true);
      const response = await apiGet<any>('/jobs?category=teaching');
      const data = handleApiResponse(response);
      // handleApiResponse already extracts data.data, so data is the actual jobs array or paginated result
      const jobsList = Array.isArray(data) ? data : (data.jobs || data.data || []);
      setJobs(jobsList);
    } catch (err: any) {
      setError('Failed to load teaching jobs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayAdd = (field: keyof TeachingJob, value: string) => {
    if (!value.trim()) return;
    const current = (formData[field] as string[]) || [];
    if (!current.includes(value)) {
      setFormData((prev) => ({ ...prev, [field]: [...current, value] }));
    }
  };

  const handleArrayRemove = (field: keyof TeachingJob, value: string) => {
    const current = (formData[field] as string[]) || [];
    setFormData((prev) => ({ ...prev, [field]: current.filter((item) => item !== value) }));
  };

  const handleNext = () => {
    if (validateStep()) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const validateStep = () => {
    switch (activeStep) {
      case 0:
        if (!formData.title || !formData.description || !formData.location) {
          setError('Please fill in all required fields');
          return false;
        }
        break;
      case 1:
        if (formData.subjectsToTeach.length === 0 || formData.teachingLevel.length === 0) {
          setError('Please select at least one subject and one teaching level');
          return false;
        }
        break;
      case 2:
        if (formData.paymentType !== PaymentType.NEGOTIABLE) {
          if (formData.paymentRate.min <= 0 || formData.paymentRate.max <= 0) {
            setError('Please enter valid payment rates, or select "Negotiable" as the payment type');
            return false;
          }
        }
        break;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const jobData = {
        ...formData,
        category: 'teaching',
      };

      if (dialogMode === 'edit' && selectedJob?._id) {
        const response = await apiPut<any>(`/jobs/${selectedJob._id}`, jobData);
        handleApiResponse(response);
        setSuccess('Teaching job updated successfully!');
      } else {
        const response = await apiPost<any>('/jobs', jobData);
        handleApiResponse(response);
        setSuccess('Teaching job created successfully!');
      }

      setDialogOpen(false);
      resetForm();
      loadTeachingJobs();
    } catch (err: any) {
      setError(err.message || 'Failed to save teaching job');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      company: 'Excellence Coaching Hub',
      location: '',
      jobType: JobType.PART_TIME,
      category: 'teaching',
      experienceLevel: ExperienceLevel.MID_LEVEL,
      educationLevel: EducationLevel.BACHELOR,
      subjectsToTeach: [],
      paymentType: PaymentType.PER_HOUR,
      paymentRate: {
        min: 0,
        max: 0,
        currency: 'USD',
      },
      teachingMode: 'online',
      teachingLevel: [],
      classSize: '',
      curriculum: '',
      languageRequirements: [],
      certificationRequired: [],
      requirements: [],
      responsibilities: [],
      status: JobStatus.ACTIVE,
      isTeachingJob: true,
    });
    setActiveStep(0);
    setNewSubject('');
    setNewRequirement('');
    setNewResponsibility('');
  };

  const handleCreateNew = () => {
    setDialogMode('create');
    setSelectedJob(null);
    resetForm();
    setDialogOpen(true);
  };

  const handleEdit = (job: TeachingJob) => {
    setDialogMode('edit');
    setSelectedJob(job);
    setFormData(job);
    setActiveStep(0);
    setDialogOpen(true);
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Job Title *"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="e.g., Mathematics Teacher for High School"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Company/Organization *"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Location *"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="e.g., Kigali, Rwanda or Remote"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Job Description *"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe the teaching position, responsibilities, and what you're looking for..."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => handleInputChange('status', e.target.value)}
                >
                  <MenuItem value={JobStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={JobStatus.DRAFT}>Draft</MenuItem>
                  <MenuItem value={JobStatus.PAUSED}>Paused</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Application Deadline"
                  value={formData.applicationDeadline}
                  onChange={(date) => handleInputChange('applicationDeadline', date)}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom>
                Subjects to Teach *
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Add a subject..."
                  value={newSubject}
                  onChange={(e) => setNewSubject(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleArrayAdd('subjectsToTeach', newSubject);
                      setNewSubject('');
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    handleArrayAdd('subjectsToTeach', newSubject);
                    setNewSubject('');
                  }}
                >
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                {COMMON_SUBJECTS.map((subject) => (
                  <Chip
                    key={subject}
                    label={subject}
                    onClick={() => handleArrayAdd('subjectsToTeach', subject)}
                    color={formData.subjectsToTeach.includes(subject) ? 'primary' : 'default'}
                    clickable
                  />
                ))}
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Selected subjects:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.subjectsToTeach.map((subject) => (
                  <Chip
                    key={subject}
                    label={subject}
                    onDelete={() => handleArrayRemove('subjectsToTeach', subject)}
                    color="primary"
                  />
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Teaching Level *
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {TEACHING_LEVELS.map((level) => (
                  <Chip
                    key={level}
                    label={level}
                    onClick={() => {
                      const current = formData.teachingLevel;
                      if (current.includes(level)) {
                        handleArrayRemove('teachingLevel', level);
                      } else {
                        handleArrayAdd('teachingLevel', level);
                      }
                    }}
                    color={formData.teachingLevel.includes(level) ? 'success' : 'default'}
                    clickable
                  />
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Teaching Mode *</InputLabel>
                <Select
                  value={formData.teachingMode}
                  label="Teaching Mode *"
                  onChange={(e) => handleInputChange('teachingMode', e.target.value)}
                >
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="in_person">In-Person</MenuItem>
                  <MenuItem value="hybrid">Hybrid</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Class Size (optional)"
                value={formData.classSize}
                onChange={(e) => handleInputChange('classSize', e.target.value)}
                placeholder="e.g., 10-20 students"
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Curriculum (optional)"
                value={formData.curriculum}
                onChange={(e) => handleInputChange('curriculum', e.target.value)}
                placeholder="e.g., Cambridge, IB, Rwandan National Curriculum"
              />
            </Grid>
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Payment Type *</InputLabel>
                <Select
                  value={formData.paymentType}
                  label="Payment Type *"
                  onChange={(e) => handleInputChange('paymentType', e.target.value)}
                >
                  <MenuItem value={PaymentType.PER_HOUR}>Per Hour</MenuItem>
                  <MenuItem value={PaymentType.PER_SESSION}>Per Session</MenuItem>
                  <MenuItem value={PaymentType.PER_MONTH}>Per Month</MenuItem>
                  <MenuItem value={PaymentType.PER_COURSE}>Per Course</MenuItem>
                  <MenuItem value={PaymentType.FIXED_SALARY}>Fixed Salary</MenuItem>
                  <MenuItem value={PaymentType.NEGOTIABLE}>Negotiable</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.paymentRate.currency}
                  label="Currency"
                  onChange={(e) =>
                    handleInputChange('paymentRate', {
                      ...formData.paymentRate,
                      currency: e.target.value,
                    })
                  }
                >
                  {CURRENCIES.map((curr) => (
                    <MenuItem key={curr.code} value={curr.code}>
                      {curr.symbol} - {curr.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={formData.paymentType === PaymentType.NEGOTIABLE ? 'Minimum Rate (Optional)' : 'Minimum Rate *'}
                type="number"
                value={formData.paymentRate.min || ''}
                disabled={formData.paymentType === PaymentType.NEGOTIABLE}
                onChange={(e) =>
                  handleInputChange('paymentRate', {
                    ...formData.paymentRate,
                    min: parseFloat(e.target.value) || 0,
                  })
                }
                helperText={formData.paymentType === PaymentType.NEGOTIABLE ? 'Not required for negotiable payment' : ''}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label={formData.paymentType === PaymentType.NEGOTIABLE ? 'Maximum Rate (Optional)' : 'Maximum Rate *'}
                type="number"
                value={formData.paymentRate.max || ''}
                disabled={formData.paymentType === PaymentType.NEGOTIABLE}
                onChange={(e) =>
                  handleInputChange('paymentRate', {
                    ...formData.paymentRate,
                    max: parseFloat(e.target.value) || 0,
                  })
                }
                helperText={formData.paymentType === PaymentType.NEGOTIABLE ? 'Not required for negotiable payment' : ''}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Language Requirements
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {LANGUAGES.map((lang) => (
                  <Chip
                    key={lang}
                    label={lang}
                    onClick={() => {
                      const current = formData.languageRequirements;
                      if (current.includes(lang)) {
                        handleArrayRemove('languageRequirements', lang);
                      } else {
                        handleArrayAdd('languageRequirements', lang);
                      }
                    }}
                    color={formData.languageRequirements.includes(lang) ? 'info' : 'default'}
                    clickable
                  />
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Required Certifications
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {CERTIFICATIONS.map((cert) => (
                  <Chip
                    key={cert}
                    label={cert}
                    onClick={() => {
                      const current = formData.certificationRequired;
                      if (current.includes(cert)) {
                        handleArrayRemove('certificationRequired', cert);
                      } else {
                        handleArrayAdd('certificationRequired', cert);
                      }
                    }}
                    color={formData.certificationRequired.includes(cert) ? 'warning' : 'default'}
                    clickable
                  />
                ))}
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                Requirements
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Add a requirement..."
                  value={newRequirement}
                  onChange={(e) => setNewRequirement(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleArrayAdd('requirements', newRequirement);
                      setNewRequirement('');
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    handleArrayAdd('requirements', newRequirement);
                    setNewRequirement('');
                  }}
                >
                  Add
                </Button>
              </Box>
              <List dense>
                {formData.requirements.map((req, idx) => (
                  <ListItem
                    key={idx}
                    secondaryAction={
                      <IconButton edge="end" onClick={() => handleArrayRemove('requirements', req)}>
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={req} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Review Teaching Job
            </Typography>
            <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Job Title
                  </Typography>
                  <Typography variant="h6">{formData.title}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Company
                  </Typography>
                  <Typography>{formData.company}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Location
                  </Typography>
                  <Typography>{formData.location}</Typography>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Subjects
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {formData.subjectsToTeach.map((s) => (
                      <Chip key={s} label={s} size="small" />
                    ))}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Teaching Mode
                  </Typography>
                  <Typography sx={{ textTransform: 'capitalize' }}>
                    {formData.teachingMode.replace('_', '-')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Payment
                  </Typography>
                  <Typography>
                    {formData.paymentType === PaymentType.NEGOTIABLE
                      ? 'Negotiable'
                      : `${formatCurrency(formData.paymentRate.min, formData.paymentRate.currency)} - ${formatCurrency(formData.paymentRate.max, formData.paymentRate.currency)} ${formData.paymentType.replace(/_/g, ' ')}`}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)' }}>
        <CardContent sx={{ color: 'white' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h5" gutterBottom>
                <School sx={{ mr: 1, verticalAlign: 'middle' }} />
                Teaching Jobs Management
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Create and manage teaching positions with specific requirements for educators
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={loadTeachingJobs}
                disabled={loading}
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  '&:hover': { borderColor: 'rgba(255,255,255,0.8)', bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateNew}
                sx={{
                  bgcolor: 'white',
                  color: '#16a34a',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                }}
              >
                Create Teaching Job
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Jobs List */}
      <Grid container spacing={3}>
        {jobs.map((job) => (
          <Grid size={{ xs: 12, md: 6, lg: 4 }} key={job._id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: (theme) => theme.shadows[8],
                },
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {job.title}
                  </Typography>
                  <Chip
                    label={job.status}
                    size="small"
                    color={job.status === JobStatus.ACTIVE ? 'success' : 'default'}
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <LocationOn fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {job.location}
                </Typography>

                <Box sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Subjects:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {job.subjectsToTeach?.slice(0, 3).map((s) => (
                      <Chip key={s} label={s} size="small" variant="outlined" />
                    ))}
                    {job.subjectsToTeach && job.subjectsToTeach.length > 3 && (
                      <Chip label={`+${job.subjectsToTeach.length - 3}`} size="small" />
                    )}
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary">
                  <AttachMoney fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {job.paymentType === PaymentType.NEGOTIABLE
                    ? 'Negotiable'
                    : job.paymentRate?.min && job.paymentRate?.max
                      ? `${formatCurrency(job.paymentRate.min, job.paymentRate.currency)} - ${formatCurrency(job.paymentRate.max, job.paymentRate.currency)} ${job.paymentType?.replace(/_/g, ' ')}`
                      : job.paymentType?.replace(/_/g, ' ') || 'Not specified'}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Chip
                    label={job.teachingMode?.replace('_', '-')}
                    size="small"
                    sx={{ mr: 1 }}
                  />
                </Box>
              </CardContent>

              <Box sx={{ p: 2, pt: 0, display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Edit />}
                  onClick={() => handleEdit(job)}
                  fullWidth
                >
                  Edit
                </Button>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <School color="success" />
            {dialogMode === 'create' ? 'Create Teaching Job' : 'Edit Teaching Job'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ minHeight: 300 }}>{renderStepContent()}</Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          <Button disabled={activeStep === 0} onClick={handleBack} sx={{ mr: 1 }}>
            Back
          </Button>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={<CheckCircle />}
            >
              {loading ? 'Saving...' : dialogMode === 'create' ? 'Create Job' : 'Update Job'}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext} endIcon={<ArrowForward />}>
              Next
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeachingJobManagement;
